import React, { createContext, useContext } from 'react';
import { destinations as albaniaDestinations, regions as albaniaRegions, categories, type Destination } from '@/data/destinations';
import { experiences as albaniaExperiences, type Experience } from '@/data/experiences';
import { destinationsRomania, regionsRomania } from '@/data/destinations.romania';
import { experiencesRomania } from '@/data/experiences.romania';

export type Country = 'albania' | 'romania';

export type ValueIcon = 'heart' | 'leaf' | 'globe' | 'shield';

export interface CountryStat {
  value: number;
  suffix: string;
  label: string;
}

export interface CountryValue {
  icon: ValueIcon;
  title: string;
  description: string;
}

export interface CountryMapRegion {
  id: string;
  name: string;
  /** Destination regions that belong under this map region. */
  match: string[];
}

export interface CountryOption {
  value: string;
  label: string;
}

export interface IntroScene {
  video: string;
  poster: string;
  title: string;
  subtitle: string;
  duration: number;
}

export interface IntroStat {
  number: string;
  label: string;
}


/**
 * All country-specific copy/config lives here so there is a single source of
 * truth. Shared components read this via `useCountry()`. Albania is the default
 * context, so any component rendered without a provider keeps its original copy.
 */
export interface CountryContent {
  /** Local "thank you" word used in success messages. */
  thanksWord: string;

  // About section
  aboutAccentLabel: string;
  aboutAccentTagline: string;
  aboutImage: string;
  aboutImageAlt: string;
  stats: CountryStat[];
  values: CountryValue[];

  // Destination map
  mapTitle: string;
  mapIntro: string;
  mapCaption: string;
  mapImage: string;
  mapImageAlt: string;
  mapEmptyText: string;
  mapRegions: CountryMapRegion[];

  // Plan journey form
  formEyebrow: string;
  formIntro: string;
  formStep2Subtitle: string;
  formStep3Subtitle: string;
  formSpecialPlaceholder: string;
  formSuccessText: string;
  experienceOptions: CountryOption[];

  // Footer
  footerNewsletterTitle: string;
  footerNewsletterDesc: string;
  footerThanks: string;
  footerThanksSub: string;
  footerContactCities: string;
  footerCopyright: string;
  // Intro film
  introTitle: string;
  introEyebrow: string;
  introDescription: string;
  introPlaySub: string;
  introScenes: IntroScene[];
  introStats: IntroStat[];
}


export interface CountryData {
  country: Country;
  /** Display label, e.g. "Albania" */
  label: string;
  /** Short tagline shown under the brand */
  tagline: string;
  /** Base route for this country's site, e.g. "/albania" */
  basePath: string;
  destinations: Destination[];
  experiences: Experience[];
  regions: string[];
  categories: typeof categories;
  content: CountryContent;
}

const albaniaContent: CountryContent = {
  thanksWord: 'Faleminderit',
  aboutAccentLabel: 'Albania',
  aboutAccentTagline: "Europe's Last Undiscovered Gem",
  aboutImage: 'https://d64gsuwffb70l.cloudfront.net/69cbf6b38dc4f4af5d55476d_1774977388422_c59b90bb.jpg',
  aboutImageAlt:
    'Zera Travel luxury Albania journey: curated Albanian Riviera, boutique hotels, private villas and bespoke Mediterranean experiences',
  stats: [
    { value: 12, suffix: '', label: 'Albanian Destinations' },
    { value: 450, suffix: 'km', label: 'of Coastline' },
    { value: 6, suffix: '', label: 'Signature Experiences' },
    { value: 24, suffix: '/7', label: 'Concierge Service' },
  ],
  values: [
    {
      icon: 'heart',
      title: 'Authentic Albania',
      description:
        'We go beyond the surface to reveal the real Albania: its warmth, traditions, and hidden treasures known only to locals.',
    },
    {
      icon: 'leaf',
      title: 'Sustainable Tourism',
      description:
        "We partner with local communities and eco-conscious properties to protect Albania's pristine landscapes for generations.",
    },
    {
      icon: 'globe',
      title: 'Cultural Immersion',
      description:
        "From Ottoman heritage cities to alpine villages, we honor Albania's rich tapestry of cultures and centuries of history.",
    },
    {
      icon: 'shield',
      title: 'Bespoke Journeys',
      description:
        'Every itinerary is handcrafted to your desires, whether you seek Riviera relaxation, mountain adventure, or cultural discovery.',
    },
  ],
  mapTitle: 'Discover Albania',
  mapIntro:
    'From the sun-kissed Riviera to the dramatic northern Alps — a hand-illustrated guide to the regions, heritage cities, castles and natural wonders awaiting you across Albania.',
  mapCaption: 'A Curated Cartography of Albania',
  mapImage: 'https://d64gsuwffb70l.cloudfront.net/69c20284ac08f4efe2afa592_1782928005706_bed4c51d.png',
  mapImageAlt:
    'Illustrated map of Albania showing the Ionian and Adriatic coastline, the northern Alps, and key destinations including Tirana, Berat, Gjirokastër, Saranda, Ksamil and Theth',
  mapEmptyText:
    'Select a region above to reveal the curated destinations our concierge team recommends across that corner of Albania.',
  mapRegions: [
    { id: 'northern-albania', name: 'Northern Albania', match: ['Northern Albania'] },
    { id: 'central-albania', name: 'Central Albania', match: ['Central Albania'] },
    { id: 'southern-albania', name: 'Southern Albania', match: ['Southern Albania', 'Albanian Riviera'] },
  ],
  formEyebrow: 'Begin Your Albanian Story',
  formIntro:
    'Share your vision and let our Albanian travel experts craft an experience beyond your imagination.',
  formStep2Subtitle: 'Where in Albania would you like to go',
  formStep3Subtitle: 'Help us personalize your Albanian experience',
  formSpecialPlaceholder:
    'Dietary requirements, celebration details, accessibility needs, preferred activities, interest in specific Albanian regions...',
  formSuccessText:
    'Your Albanian journey request has been received. Our dedicated concierge team will craft a personalized itinerary and reach out to you within 24 hours at',
  experienceOptions: [
    { value: 'albanian-riviera', label: 'Albanian Riviera' },
    { value: 'ottoman-heritage', label: 'Ottoman Heritage Tour' },
    { value: 'alps-trek', label: 'Albanian Alps Trek' },
    { value: 'culinary-albania', label: 'Culinary Albania' },
    { value: 'archaeological-odyssey', label: 'Archaeological Odyssey' },
    { value: 'boutique-retreat', label: 'Boutique Retreat' },
  ],
  footerNewsletterTitle: 'Stay Inspired by Albania',
  footerNewsletterDesc:
    'Receive curated Albanian travel inspiration, seasonal highlights, and exclusive access to our newest experiences.',
  footerThanks: 'Faleminderit! Welcome to the Zera world.',
  footerThanksSub: "You'll receive our next Albanian travel collection shortly.",
  footerContactCities: 'Tirana | Sarandë | Shkodër',
  footerCopyright: 'Zera Travel Albania',
  introTitle: 'Experience Albania',
  introEyebrow: 'A Cinematic Journey',
  introDescription:
    'A one-minute luxury film showcasing the coastlines, cities, mountains, villas, and yacht charters that define the Zera Travel experience.',
  introPlaySub: 'Luxury Albania Experience',
  introScenes: [
    {
      video: 'https://videos.pexels.com/video-files/1093662/1093662-uhd_2560_1440_30fps.mp4',
      poster: 'https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=1920&q=80',
      title: 'The Albanian Riviera',
      subtitle: 'Crystal waters rivaling the Maldives, without the crowds',
      duration: 6,
    },
    {
      video: 'https://videos.pexels.com/video-files/6394054/6394054-uhd_2560_1440_25fps.mp4',
      poster: 'https://images.unsplash.com/photo-1464822759023-fed622ff2c3b?w=1920&q=80',
      title: 'The Accursed Mountains',
      subtitle: 'Dramatic alpine peaks and emerald valleys await the adventurous',
      duration: 6,
    },
    {
      video: 'https://videos.pexels.com/video-files/2491284/2491284-uhd_2560_1440_24fps.mp4',
      poster: 'https://images.unsplash.com/photo-1544551763-46a013bb70d5?w=1920&q=80',
      title: 'Natural Wonders',
      subtitle: "The Blue Eye, nature's own impossibly vivid jewel",
      duration: 6,
    },
    {
      video: 'https://videos.pexels.com/video-files/4763824/4763824-uhd_2560_1440_24fps.mp4',
      poster: 'https://images.unsplash.com/photo-1613490493576-7fde63acd811?w=1920&q=80',
      title: 'Private Villa Collection',
      subtitle: 'Handpicked estates with panoramic Mediterranean views',
      duration: 6,
    },
    {
      video: 'https://videos.pexels.com/video-files/3015510/3015510-hd_1920_1080_24fps.mp4',
      poster: 'https://images.unsplash.com/photo-1567899378494-47b22a2ae96a?w=1920&q=80',
      title: 'Yacht Charters',
      subtitle: 'Cruise hidden coves and secret beaches by private vessel',
      duration: 6,
    },
    {
      video: 'https://videos.pexels.com/video-files/3571264/3571264-uhd_2560_1440_30fps.mp4',
      poster: 'https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=1920&q=80',
      title: 'Culinary Excellence',
      subtitle: 'Farm-to-table Mediterranean cuisine crafted by local artisans',
      duration: 6,
    },
    {
      video: 'https://videos.pexels.com/video-files/2169880/2169880-uhd_2560_1440_24fps.mp4',
      poster: 'https://images.unsplash.com/photo-1506929562872-bb421503ef21?w=1920&q=80',
      title: 'Your Journey Begins',
      subtitle: 'Zera Travel. Bespoke Albanian experiences, crafted for the extraordinary',
      duration: 6,
    },
  ],
  introStats: [
    { number: '362', label: 'Pristine Beaches' },
    { number: '3', label: 'UNESCO Sites' },
    { number: '300+', label: 'Days of Sunshine' },
    { number: '2,500+', label: 'Years of History' },
  ],
};


const romaniaContent: CountryContent = {
  thanksWord: 'Mulțumesc',
  aboutAccentLabel: 'Romania',
  aboutAccentTagline: 'Land of Castles & the Carpathians',
  aboutImage: 'https://d64gsuwffb70l.cloudfront.net/69cbf6b38dc4f4af5d55476d_1782406711915_8dbdbfed.jpg',
  aboutImageAlt:
    'Zera Travel luxury Romania journey: Peleș Castle in the Carpathians, painted monasteries, medieval Saxon citadels and bespoke Transylvanian experiences',
  stats: [
    { value: 12, suffix: '', label: 'Romanian Destinations' },
    { value: 8, suffix: '', label: 'UNESCO Sites' },
    { value: 6, suffix: '', label: 'Signature Experiences' },
    { value: 24, suffix: '/7', label: 'Concierge Service' },
  ],
  values: [
    {
      icon: 'heart',
      title: 'Authentic Romania',
      description:
        'We go beyond the surface to reveal the real Romania: its folklore, village traditions, and hidden treasures known only to locals.',
    },
    {
      icon: 'leaf',
      title: 'Sustainable Tourism',
      description:
        "We partner with local communities and eco-conscious properties to protect Romania's Carpathian wilderness and painted heritage for generations.",
    },
    {
      icon: 'globe',
      title: 'Cultural Immersion',
      description:
        "From medieval Saxon citadels to painted monasteries, we honor Romania's rich tapestry of cultures and centuries of history.",
    },
    {
      icon: 'shield',
      title: 'Bespoke Journeys',
      description:
        'Every itinerary is handcrafted to your desires, whether you seek castle trails, Carpathian adventure, or Danube Delta discovery.',
    },
  ],
  mapTitle: 'Discover Romania',
  mapIntro:
    'From the medieval citadels of Transylvania to the painted monasteries of Bucovina and the wild Danube Delta — a hand-illustrated guide to the regions, castles and natural wonders awaiting you across Romania.',
  mapCaption: 'A Curated Cartography of Romania',
  mapImage: 'https://d64gsuwffb70l.cloudfront.net/69c20284ac08f4efe2afa592_1782928292510_12f21bb8.png',
  mapImageAlt:
    'Illustrated map of Romania showing the Carpathian Mountains, Transylvania, the Danube Delta and the Black Sea coast with key destinations including Bucharest, Brașov, Sibiu, Sighișoara, Bran Castle, Peleș Castle and the Painted Monasteries of Bucovina',
  mapEmptyText:
    'Select a region above to reveal the curated destinations our concierge team recommends across that corner of Romania.',
  mapRegions: [
    { id: 'transylvania-carpathians', name: 'Transylvania & Carpathians', match: ['Transylvania', 'Carpathian Mountains', 'Banat'] },
    { id: 'bucovina-north', name: 'Bucovina & the North', match: ['Bucovina'] },
    { id: 'wallachia-coast', name: 'Wallachia & Black Sea', match: ['Wallachia', 'Danube Delta', 'Black Sea Coast'] },
  ],
  formEyebrow: 'Begin Your Romanian Story',
  formIntro:
    'Share your vision and let our Romanian travel experts craft an experience beyond your imagination.',
  formStep2Subtitle: 'Where in Romania would you like to go',
  formStep3Subtitle: 'Help us personalize your Romanian experience',
  formSpecialPlaceholder:
    'Dietary requirements, celebration details, accessibility needs, preferred activities, interest in specific Romanian regions...',
  formSuccessText:
    'Your Romanian journey request has been received. Our dedicated concierge team will craft a personalized itinerary and reach out to you within 24 hours at',
  experienceOptions: [
    { value: 'transylvania-dracula', label: 'Transylvania & Dracula Trail' },
    { value: 'carpathian-adventure', label: 'Carpathian Mountain Adventure' },
    { value: 'painted-monasteries', label: 'Painted Monasteries of Bucovina' },
    { value: 'danube-delta', label: 'Danube Delta Safari' },
    { value: 'culinary-romania', label: 'Culinary Romania' },
    { value: 'saxon-villages', label: 'Saxon Villages & Boutique Stays' },
  ],
  footerNewsletterTitle: 'Stay Inspired by Romania',
  footerNewsletterDesc:
    'Receive curated Romanian travel inspiration, seasonal highlights, and exclusive access to our newest experiences.',
  footerThanks: 'Mulțumesc! Welcome to the Zera world.',
  footerThanksSub: "You'll receive our next Romanian travel collection shortly.",
  footerContactCities: 'Bucharest | Brașov | Sibiu',
  footerCopyright: 'Zera Travel Romania',
  introTitle: 'Experience Romania',
  introEyebrow: 'A Cinematic Journey',
  introDescription:
    'A one-minute luxury film showcasing the castles, painted monasteries, Carpathian peaks, Saxon citadels, and wild Danube Delta that define the Zera Travel experience in Romania.',
  introPlaySub: 'Luxury Romania Experience',
  introScenes: [
    {
      video: 'https://videos.pexels.com/video-files/2491284/2491284-uhd_2560_1440_24fps.mp4',
      poster: 'https://d64gsuwffb70l.cloudfront.net/69cbf6b38dc4f4af5d55476d_1782409130737_b3138828.jpg',
      title: 'Bran Castle, Transylvania',
      subtitle: "The Gothic fortress at the heart of the Dracula legend",
      duration: 6,
    },
    {
      video: 'https://videos.pexels.com/video-files/4763824/4763824-uhd_2560_1440_24fps.mp4',
      poster: 'https://d64gsuwffb70l.cloudfront.net/69cbf6b38dc4f4af5d55476d_1782409151748_a92f0f75.jpg',
      title: 'Peleș Castle, Sinaia',
      subtitle: 'A fairytale royal palace nestled in the Carpathian forests',
      duration: 6,
    },
    {
      video: 'https://videos.pexels.com/video-files/6394054/6394054-uhd_2560_1440_25fps.mp4',
      poster: 'https://d64gsuwffb70l.cloudfront.net/69cbf6b38dc4f4af5d55476d_1782409221957_42a827cb.png',
      title: 'The Carpathian Mountains',
      subtitle: 'The legendary Transfăgărășan road winding through alpine peaks',
      duration: 6,
    },
    {
      video: 'https://videos.pexels.com/video-files/3571264/3571264-uhd_2560_1440_30fps.mp4',
      poster: 'https://d64gsuwffb70l.cloudfront.net/69cbf6b38dc4f4af5d55476d_1782409198928_a6ed4497.png',
      title: 'Painted Monasteries of Bucovina',
      subtitle: 'Vivid 15th-century frescoes glowing in famous Voroneț blue',
      duration: 6,
    },
    {
      video: 'https://videos.pexels.com/video-files/3015510/3015510-hd_1920_1080_24fps.mp4',
      poster: 'https://d64gsuwffb70l.cloudfront.net/69c20284ac08f4efe2afa592_1782757844096_10387d6c.jpeg',
      title: 'The Danube Delta',
      subtitle: "Europe's wildest wetland, home to over 300 bird species",
      duration: 6,
    },
    {
      video: 'https://videos.pexels.com/video-files/2169880/2169880-uhd_2560_1440_24fps.mp4',
      poster: 'https://d64gsuwffb70l.cloudfront.net/69cbf6b38dc4f4af5d55476d_1782409263654_a4d433fe.jpg',
      title: 'Saxon Citadels of Sighișoara',
      subtitle: 'Cobblestoned medieval streets and pastel-hued towers',
      duration: 6,
    },
    {
      video: 'https://videos.pexels.com/video-files/1093662/1093662-uhd_2560_1440_30fps.mp4',
      poster: 'https://d64gsuwffb70l.cloudfront.net/69cbf6b38dc4f4af5d55476d_1782409288633_17c8756e.png',
      title: 'Your Journey Begins',
      subtitle: 'Zera Travel. Bespoke Romanian experiences, crafted for the extraordinary',
      duration: 6,
    },
  ],
  introStats: [
    { number: '8', label: 'UNESCO Sites' },
    { number: '160+', label: 'Castles & Citadels' },
    { number: '6,000', label: 'Wild Brown Bears' },
    { number: '2,000+', label: 'Years of History' },
  ],
};


const albaniaData: CountryData = {
  country: 'albania',
  label: 'Albania',
  tagline: 'Europe\'s Hidden Riviera',
  basePath: '/albania',
  destinations: albaniaDestinations,
  experiences: albaniaExperiences,
  regions: albaniaRegions,
  categories,
  content: albaniaContent,
};

const romaniaData: CountryData = {
  country: 'romania',
  label: 'Romania',
  tagline: 'Land of the Carpathians',
  basePath: '/romania',
  destinations: destinationsRomania,
  experiences: experiencesRomania,
  regions: regionsRomania,
  categories,
  content: romaniaContent,
};

export const countryDataMap: Record<Country, CountryData> = {
  albania: albaniaData,
  romania: romaniaData,
};

// Default to Albania so standalone pages (luxury-travel, blog, etc.) that render
// shared components without a provider continue to work exactly as before.
const CountryContext = createContext<CountryData>(albaniaData);

export const CountryProvider: React.FC<{ country: Country; children: React.ReactNode }> = ({ country, children }) => {
  return (
    <CountryContext.Provider value={countryDataMap[country]}>
      {children}
    </CountryContext.Provider>
  );
};

export const useCountry = (): CountryData => useContext(CountryContext);

export default CountryContext;
